import { Facet, FacetSelections, PlpQueryResult, SortAttributes } from 'helpers/server/fetch-products';
import { PlpFilterGroup } from './plp-filter-sidebar';
import { PlpProduct } from './plp-product-card';

// Shared data-shaping for the brand PLP, used by both the category PLP and the search results page
// so the two render from an identical pipeline. Kept out of the components so each caller (a server
// component) can run the queries and pass plain data down.

// The filterable facets are the product's own variant attributes that have options for this result
// set — derived from the live facets rather than a hardcoded list, so every page filters itself.
export const isFilterableFacet = (facet: Facet) =>
  facet.identifier.startsWith('variants.attributes.') && !!facet.terms?.length;

// Selected filters come straight from the URL — each facet param is a `variants.attributes.*` key
// with comma-separated values — so callers don't need the facet-options query to resolve first.
export const parseFacetSelections = (query: Record<string, string | string[] | undefined>): FacetSelections =>
  Object.fromEntries(
    Object.keys(query)
      .filter((key) => key.startsWith('variants.attributes.'))
      .map((key) => {
        const raw = query[key];
        const value = Array.isArray(raw) ? raw.join(',') : raw;
        return [key, { terms: (value ?? '').split(',').filter(Boolean) }] as const;
      })
      .filter(([, selection]) => selection.terms.length),
  );

// Pagination lives in the URL like the filters. `page` is 1-indexed; the backend paginates by an
// `offset:<n>` cursor.
export const resolvePage = (query: Record<string, string | string[] | undefined>, pageSize: number) => {
  const rawPage = Array.isArray(query.page) ? query.page[0] : query.page;
  const currentPage = Math.max(1, Number.parseInt(rawPage ?? '1', 10) || 1);
  const cursor = currentPage > 1 ? `offset:${(currentPage - 1) * pageSize}` : undefined;
  return { currentPage, cursor };
};

export const toFilterGroups = (result: PlpQueryResult | undefined): PlpFilterGroup[] =>
  (result?.facets ?? [])
    .filter(isFilterableFacet)
    .map((facet): PlpFilterGroup | null => {
      const options = (facet.terms ?? [])
        .map((term) => ({ label: term.label ?? term.key ?? '', value: term.key ?? term.label ?? '', count: term.count }))
        .filter((option) => option.value);
      if (!options.length) return null;
      return { label: facet.label, identifier: facet.identifier, options };
    })
    .filter((group): group is PlpFilterGroup => group !== null);

export const toPlpProducts = (result: PlpQueryResult | undefined): PlpProduct[] => result?.items ?? [];

export const totalOf = (result: PlpQueryResult | undefined): number => result?.total ?? 0;

// Sort lives in the URL as `sort=<value>` alongside the filters. The default (no param) leaves the
// backend's own order in place: Merchant Center ranking on a category, relevance on search — which
// is why the default option is labelled per caller rather than here.
export interface PlpSortOption {
  value: string;
  label: string;
}

export const PLP_DEFAULT_SORT = 'featured';

export const PLP_SORT_OPTIONS: PlpSortOption[] = [
  { value: PLP_DEFAULT_SORT, label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
];

// Backend sort attributes per option. Keyed `sortAttributes[0][<field>]=<order>` — the same shape the
// legacy product list sends, which ProductQueryFactory maps onto the commercetools search sort.
const SORT_ATTRIBUTES: Record<string, SortAttributes | undefined> = {
  'price-asc': { 0: { price: 'asc' } },
  'price-desc': { 0: { price: 'desc' } },
};

export const resolveSort = (query: Record<string, string | string[] | undefined>) => {
  const raw = Array.isArray(query.sort) ? query.sort[0] : query.sort;
  const sort = PLP_SORT_OPTIONS.some((option) => option.value === raw) ? (raw as string) : PLP_DEFAULT_SORT;
  return { sort, sortAttributes: SORT_ATTRIBUTES[sort] };
};
